import { FunnelStartedFields, TestStartedFields, UserClickFields, ViewContentFields } from '@xylabs/pixel'
import { ProcessPaymentResult } from '@xylabs/sdk-coin-js'

import { CoinEventHandler } from './CoinEventHandler'
import { CoinUserEventHandlerBase } from './CoinUserEventHandlerBase'
import { DripEventHandler } from './DripEventHandler'
import { FacebookEventHandler } from './FacebookEventHandler'
import { GoogleEventHandler } from './GoogleEventHandler'
import { RedditEventHandler } from './RedditEventHandler'
import { SnapchatEventHandler } from './SnapchatEventHandler'
// import { TapjoyEventHandler } from './TapjoyEventHandler'
import { TiktokEventHandler } from './TiktokEventHandler'

export class UserEvents<T extends Record<string, unknown>> extends CoinUserEventHandlerBase<T> {
  static generateEventId(length: number) {
    let result = ''
    const characters = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789'
    const charactersLength = characters.length
    for (let i = 0; i < length; i++) {
      result += characters.charAt(Math.floor(Math.random() * charactersLength))
    }
    return result
  }

  async emailCollected(fields: { email: string }) {
    const eventId = UserEvents.generateEventId(8)
    await Promise.allSettled(this.handlers.map((handler) => handler.registerEventId(eventId).emailCollected(fields)))
  }
  async accountCreated() {
    const eventId = UserEvents.generateEventId(8)
    await Promise.allSettled(this.handlers.map((handler) => handler.registerEventId(eventId).accountCreated()))
  }
  async shareCompleted() {
    const eventId = UserEvents.generateEventId(8)
    await Promise.allSettled(this.handlers.map((handler) => handler.registerEventId(eventId).shareCompleted()))
  }
  async buyticketsCompleted() {
    const eventId = UserEvents.generateEventId(8)
    await Promise.allSettled(this.handlers.map((handler) => handler.registerEventId(eventId).buyticketsCompleted()))
  }
  async installCompleted() {
    const eventId = UserEvents.generateEventId(8)
    await Promise.allSettled(this.handlers.map((handler) => handler.registerEventId(eventId).installCompleted()))
  }
  async funnelCompleted() {
    const eventId = UserEvents.generateEventId(8)
    await Promise.allSettled(this.handlers.map((handler) => handler.registerEventId(eventId).funnelCompleted()))
  }
  async offerRequested(fields: { id: string }) {
    const eventId = UserEvents.generateEventId(8)
    await Promise.allSettled(this.handlers.map((handler) => handler.registerEventId(eventId).offerRequested(fields)))
  }
  async feedbackScoreCollected(fields: { score: number | string }) {
    const eventId = UserEvents.generateEventId(8)
    await Promise.allSettled(this.handlers.map((handler) => handler.registerEventId(eventId).feedbackScoreCollected(fields)))
  }
  async feedbackSourceCollected(fields: { source: string }) {
    const eventId = UserEvents.generateEventId(8)
    await Promise.allSettled(this.handlers.map((handler) => handler.registerEventId(eventId).feedbackSourceCollected(fields)))
  }
  async goodFeedbackRequested() {
    const eventId = UserEvents.generateEventId(8)
    await Promise.allSettled(this.handlers.map((handler) => handler.registerEventId(eventId).goodFeedbackRequested()))
  }
  async badFeedbackRequested() {
    const eventId = UserEvents.generateEventId(8)
    await Promise.allSettled(this.handlers.map((handler) => handler.registerEventId(eventId).badFeedbackRequested()))
  }

  private static getParameterByName(name: string, url = window.location.href) {
    name = name.replace(/[\[\]]/g, '\\$&')
    const regex = new RegExp('[?&]' + name + '(=([^&#]*)|&|#|$)'),
      results = regex.exec(url)
    if (!results) return null
    if (!results[2]) return ''
    return decodeURIComponent(results[2].replace(/\+/g, ' '))
  }

  protected handlers: CoinUserEventHandlerBase<T>[]
  private constructor(handlers?: CoinUserEventHandlerBase<T>[]) {
    super()
    const utmSource = UserEvents.getParameterByName('utm_source')
    if (handlers) {
      this.handlers = handlers
    } else {
      this.handlers = [
        new CoinEventHandler<T>(),
        new FacebookEventHandler<T>(),
        new GoogleEventHandler<T>(),
        new DripEventHandler<T>(),
        // new TapjoyEventHandler<T>(),
      ]
      switch (utmSource) {
        case 'tiktok':
          this.handlers.push(new TiktokEventHandler<T>())
          break
        case 'reddit':
          this.handlers.push(new RedditEventHandler<T>())
          break
        case 'snap':
          this.handlers.push(new SnapchatEventHandler<T>())
          break
      }
    }
  }

  async addToCart() {
    const eventId = UserEvents.generateEventId(8)
    await Promise.allSettled(this.handlers.map((handler) => handler.registerEventId(eventId).addToCart()))
  }

  async initiateCheckout() {
    const eventId = UserEvents.generateEventId(8)
    await Promise.allSettled(this.handlers.map((handler) => handler.registerEventId(eventId).initiateCheckout()))
  }

  async contactInfoComplete() {
    const eventId = UserEvents.generateEventId(8)
    await Promise.allSettled(this.handlers.map((handler) => handler.registerEventId(eventId).contactInfoComplete()))
  }

  async addressInfoComplete() {
    const eventId = UserEvents.generateEventId(8)
    await Promise.allSettled(this.handlers.map((handler) => handler.registerEventId(eventId).addressInfoComplete()))
  }

  async paymentInfoComplete() {
    const eventId = UserEvents.generateEventId(8)
    await Promise.allSettled(this.handlers.map((handler) => handler.registerEventId(eventId).paymentInfoComplete()))
  }

  async purchaseAttempted() {
    const eventId = UserEvents.generateEventId(8)
    await Promise.allSettled(this.handlers.map((handler) => handler.registerEventId(eventId).purchaseAttempted()))
  }

  async purchaseFailed() {
    const eventId = UserEvents.generateEventId(8)
    await Promise.allSettled(this.handlers.map((handler) => handler.registerEventId(eventId).purchaseFailed()))
  }

  async purchaseCompleted(payment: ProcessPaymentResult) {
    const eventId = UserEvents.generateEventId(8)
    await Promise.allSettled(this.handlers.map((handler) => handler.registerEventId(eventId).purchaseCompleted(payment)))
  }

  async upsellViewed() {
    const eventId = UserEvents.generateEventId(8)
    await Promise.allSettled(this.handlers.map((handler) => handler.registerEventId(eventId).upsellViewed()))
  }

  async upsellUnavailable() {
    const eventId = UserEvents.generateEventId(8)
    await Promise.allSettled(this.handlers.map((handler) => handler.registerEventId(eventId).upsellUnavailable()))
  }

  async upsellAccepted() {
    const eventId = UserEvents.generateEventId(8)
    await Promise.allSettled(this.handlers.map((handler) => handler.registerEventId(eventId).upsellAccepted()))
  }

  async upsellSucceeded() {
    const eventId = UserEvents.generateEventId(8)
    await Promise.allSettled(this.handlers.map((handler) => handler.registerEventId(eventId).upsellSucceeded()))
  }

  async upsellFailed() {
    const eventId = UserEvents.generateEventId(8)
    await Promise.allSettled(this.handlers.map((handler) => handler.registerEventId(eventId).upsellFailed()))
  }

  async trialStarted() {
    const eventId = UserEvents.generateEventId(8)
    await Promise.allSettled(this.handlers.map((handler) => handler.registerEventId(eventId).trialStarted()))
  }

  async funnelStarted(fields: FunnelStartedFields) {
    const eventId = UserEvents.generateEventId(8)
    await Promise.allSettled(this.handlers.map((handler) => handler.registerEventId(eventId).funnelStarted(fields)))
  }

  async testStarted(fields: TestStartedFields) {
    const eventId = UserEvents.generateEventId(8)
    await Promise.allSettled(this.handlers.map((handler) => handler.registerEventId(eventId).testStarted(fields)))
  }

  async viewContent(fields: ViewContentFields) {
    const eventId = UserEvents.generateEventId(8)
    await Promise.allSettled(this.handlers.map((handler) => handler.registerEventId(eventId).viewContent(fields)))
  }

  async userClick(fields: UserClickFields) {
    const eventId = UserEvents.generateEventId(8)
    await Promise.allSettled(this.handlers.map((handler) => handler.registerEventId(eventId).userClick(fields)))
  }

  async customEvent(name: string, data?: T) {
    const eventId = UserEvents.generateEventId(8)
    await Promise.allSettled(this.handlers.map((handler) => handler.registerEventId(eventId).customEvent(name, data)))
  }

  static instance: UserEvents<Record<string, unknown>>
  static get() {
    this.instance = this.instance ?? new UserEvents()
    return this.instance
  }
}
